import { useMutation, useQuery } from '@tanstack/react-query'
import { fetchGrowth } from '../api/growth'
import {
  createImmunisationRecord,
  deleteImmunisationRecord,
  fetchImmunisations,
  fetchVaccines,
  ImmunisationRecordPayload,
  updateImmunisationRecord
} from '../api/immunisations'
import { fetchPreventiveCare } from '../api/preventiveCare'
import { queryClient } from '../store/provider/TanStackProvider'

/** Query keys for the paediatrics (growth / immunisation / preventive care) screens. */
export const PAEDS_QUERY_KEYS = {
  growth: (patientId?: string) => ['growth', { patientId }] as const,
  immunisations: (patientId?: string) => ['immunisations', { patientId }] as const,
  preventiveCare: (patientId?: string) => ['preventive_care', { patientId }] as const,
  vaccines: ['vaccines'] as const
}

// The app's shared QueryClient uses staleTime: Infinity. Growth and preventive care are derived from vitals the
// nurse records on other screens, so they refetch whenever a screen that shows them mounts.
const FRESH_ON_MOUNT = { refetchOnMount: 'always' as const, staleTime: 0 }

export const useGrowth = (patientId?: string, enabled = true) => {
  const { data, isLoading, isError, error } = useQuery({
    ...FRESH_ON_MOUNT,
    enabled: !!patientId && enabled,
    queryFn: () => fetchGrowth(patientId as string),
    queryKey: PAEDS_QUERY_KEYS.growth(patientId)
  })
  return { error, growth: data, isError, isLoading }
}

export const usePreventiveCare = (patientId?: string) => {
  const { data, isLoading } = useQuery({
    ...FRESH_ON_MOUNT,
    enabled: !!patientId,
    queryFn: () => fetchPreventiveCare(patientId as string),
    queryKey: PAEDS_QUERY_KEYS.preventiveCare(patientId)
  })
  return { isLoading, preventiveCare: data }
}

export const useVaccines = () => {
  const { data } = useQuery({ queryFn: fetchVaccines, queryKey: PAEDS_QUERY_KEYS.vaccines })
  return { vaccines: data ?? [] }
}

export const useImmunisations = (patientId?: string) => {
  const { data, isLoading, isError, error } = useQuery({
    ...FRESH_ON_MOUNT,
    enabled: !!patientId,
    queryFn: () => fetchImmunisations(patientId as string),
    queryKey: PAEDS_QUERY_KEYS.immunisations(patientId)
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['immunisations'] })
    queryClient.invalidateQueries({ queryKey: ['preventive_care'] })
  }

  const { mutateAsync: createRecord, isPending: isCreating } = useMutation({
    mutationFn: (payload: ImmunisationRecordPayload) => createImmunisationRecord(payload),
    onSuccess: invalidate
  })

  /** For bulk entry: post without invalidating per row; call `refresh()` once at the end. */
  const { mutateAsync: createRecordQuiet } = useMutation({
    mutationFn: (payload: ImmunisationRecordPayload) => createImmunisationRecord(payload)
  })

  const { mutateAsync: updateRecord, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ImmunisationRecordPayload }) =>
      updateImmunisationRecord(id, payload),
    onSuccess: invalidate
  })

  const { mutateAsync: deleteRecord, isPending: isDeleting } = useMutation({
    mutationFn: (id: string) => deleteImmunisationRecord(id),
    onSuccess: invalidate
  })

  return {
    createRecord,
    createRecordQuiet,
    deleteRecord,
    error,
    immunisations: data,
    isCreating,
    isDeleting,
    isError,
    isLoading,
    isUpdating,
    refresh: invalidate,
    updateRecord
  }
}
